/**
 * Console light/dark. "auto" follows the operator's device
 * (`prefers-color-scheme`), like the hosted sign-in pages; light and dark are
 * explicit overrides. index.html applies the same rule before first paint.
 */
export type ThemePref = "auto" | "light" | "dark";
export type Theme = "light" | "dark";

export const THEME_PREF_LABEL: Record<ThemePref, string> = {
  auto: "Match device",
  light: "Light",
  dark: "Dark",
};

const ORDER: ThemePref[] = ["auto", "light", "dark"];

export function nextThemePref(pref: ThemePref): ThemePref {
  return ORDER[(ORDER.indexOf(pref) + 1) % ORDER.length] ?? "auto";
}

export function resolveTheme(pref: ThemePref, prefersDark: boolean): Theme {
  if (pref === "auto") return prefersDark ? "dark" : "light";
  return pref;
}

/**
 * The stored preference from the tweaks blob. Before "auto" existed the
 * console saved `theme: "dark"` (its default) whenever any tweak changed, so
 * that value is not a choice and follows the device; a legacy "light" was
 * always a deliberate toggle.
 */
export function readThemePref(raw: string | null): ThemePref {
  if (!raw) return "auto";
  try {
    const stored = JSON.parse(raw) as { themePref?: unknown; theme?: unknown };
    if (stored.themePref === "auto" || stored.themePref === "light" || stored.themePref === "dark") {
      return stored.themePref;
    }
    return stored.theme === "light" ? "light" : "auto";
  } catch {
    return "auto";
  }
}
